import type { FBClusterTagV4, FBTeamPlayer, User } from "./types";

/**
 * Auto-tag: match detected players to the recording and opponent team
 * rosters by jersey number. A port of mobile's
 * components/session/v4/auto-tag-roster.ts, so the two clients tag alike --
 * with one deliberate difference, in `buildAutoTagEdits`: the web never
 * overwrites a player someone tagged by hand.
 */

/** A row of `GET /session/team/{recording|opponent}/roster`, as sent. */
export type SessionTeamRosterPlayer = {
  id: number | string;
  groupRosterId: number | string;
  userId?: string | null;
  jersey?: string | number | null;
  name?: string | null;
  position?: string | null;
  visible?: number | null;
  /** From the linked account (see withRosterUserNames); rows carry none. */
  firstName?: string;
  lastName?: string;
};

/**
 * Names roster rows from their linked accounts. Adding a player to a roster
 * mints a guest account that holds the first and last name, so that is where
 * "幸希 武部" lives; the row itself has only a jersey and the account id.
 */
export const withRosterUserNames = (
  rows: SessionTeamRosterPlayer[],
  users: User[],
): SessionTeamRosterPlayer[] =>
  rows.map((row) => {
    const user = row.userId ? users.find((candidate) => candidate.userId === row.userId) : undefined;
    return user
      ? { ...row, firstName: user.firstName || undefined, lastName: user.lastName || undefined }
      : row;
  });

export const normalizeRosterJersey = (value: unknown): number | undefined => {
  if (value == null) return undefined;
  const jersey = String(value).trim();
  // The detector and the tagging UIs use -1 for double zero.
  if (jersey === "00" || jersey === "-1") return -1;
  if (!/^\d+$/.test(jersey)) return undefined;
  return Number(jersey);
};

const isPlayerCluster = (cluster: FBClusterTagV4) =>
  !cluster.ignore && cluster.clusterClass !== -1 && Boolean(cluster.clusterId);

export const findAutoTagRosterPlayer = (
  cluster: FBClusterTagV4,
  recordingTeamId: string | undefined,
  opponentTeamId: string | undefined,
  recordingRoster: SessionTeamRosterPlayer[],
  opponentRoster: SessionTeamRosterPlayer[],
): SessionTeamRosterPlayer | undefined => {
  if (!isPlayerCluster(cluster)) return undefined;
  const jersey = normalizeRosterJersey(cluster.jersey);
  if (jersey === undefined || cluster.teamV2Id == null) return undefined;

  // The team comes from the cluster's own team assignment, never a guess.
  const roster =
    recordingTeamId != null && String(cluster.teamV2Id) === String(recordingTeamId)
      ? recordingRoster
      : opponentTeamId != null && String(cluster.teamV2Id) === String(opponentTeamId)
        ? opponentRoster
        : [];
  const matches = roster.filter(
    (player) => player.visible !== 0 && normalizeRosterJersey(player.jersey) === jersey,
  );
  // Duplicate numbers need a person to tell them apart.
  return matches.length === 1 ? matches[0] : undefined;
};

/**
 * The name saved on an auto-tagged cluster. Roster rows carry a name only
 * when no account is behind the player; otherwise the linked user supplies
 * it, so no placeholder is written.
 */
export const autoTagClusterName = (
  cluster: FBClusterTagV4,
  player: SessionTeamRosterPlayer,
): string | undefined => player.name?.trim() || cluster.name;

const isTaggedByHand = (cluster: FBClusterTagV4) =>
  Boolean(cluster.userId || cluster.nameId || cluster.teamPlayerId);

export type AutoTagRosters = {
  recordingTeamId?: string;
  opponentTeamId?: string;
  recordingRoster: SessionTeamRosterPlayer[];
  opponentRoster: SessionTeamRosterPlayer[];
};

export type AutoTagPlan = {
  /** Whole clusters, as `POST .../roster/autotag-teams` expects them. */
  edits: FBClusterTagV4[];
  tagged: number;
  /** Already tagged by someone, to another player: left alone. */
  keptManual: number;
  /** Players with no team, no readable number, or no unique roster match. */
  unmatched: number;
  /** Already tagged to the roster player their number matches. */
  alreadyTagged: number;
  // Why the unmatched ones weren't: they add up to `unmatched`.
  noTeam: number;
  noNumber: number;
  duplicateNumber: number;
  /** Jersey numbers, as shown ("07"), that no one on that team's roster wears. */
  notOnRoster: string[];
};

export const emptyAutoTagPlan = (): AutoTagPlan => ({
  edits: [],
  tagged: 0,
  keptManual: 0,
  unmatched: 0,
  alreadyTagged: 0,
  noTeam: 0,
  noNumber: 0,
  duplicateNumber: 0,
  notOnRoster: [],
});

const displayJersey = (jersey: number) =>
  jersey === -1 ? "00" : String(jersey).padStart(2, "0");

/** Why a player with no roster match has none. */
const missReason = (
  cluster: FBClusterTagV4,
  rosters: AutoTagRosters,
): "noTeam" | "noNumber" | "duplicateNumber" | "notOnRoster" => {
  const jersey = normalizeRosterJersey(cluster.jersey);
  if (jersey === undefined) return "noNumber";
  const team = cluster.teamV2Id == null ? undefined : String(cluster.teamV2Id);
  const roster =
    team && team === String(rosters.recordingTeamId)
      ? rosters.recordingRoster
      : team && team === String(rosters.opponentTeamId)
        ? rosters.opponentRoster
        : undefined;
  if (!roster) return "noTeam";
  const wearers = roster.filter(
    (player) => player.visible !== 0 && normalizeRosterJersey(player.jersey) === jersey,
  );
  return wearers.length > 1 ? "duplicateNumber" : "notOnRoster";
};

export const buildAutoTagEdits = (
  clusters: FBClusterTagV4[],
  rosters: AutoTagRosters,
): AutoTagPlan => {
  const plan = emptyAutoTagPlan();
  for (const cluster of clusters) {
    if (!isPlayerCluster(cluster)) continue;
    const match = findAutoTagRosterPlayer(
      cluster,
      rosters.recordingTeamId,
      rosters.opponentTeamId,
      rosters.recordingRoster,
      rosters.opponentRoster,
    );
    if (!match) {
      if (isTaggedByHand(cluster)) {
        plan.keptManual += 1;
        continue;
      }
      plan.unmatched += 1;
      const reason = missReason(cluster, rosters);
      if (reason === "notOnRoster") {
        plan.notOnRoster.push(displayJersey(normalizeRosterJersey(cluster.jersey) as number));
      } else {
        plan[reason] += 1;
      }
      continue;
    }
    const teamPlayerId = String(match.id);
    if (cluster.teamPlayerId === teamPlayerId) {
      plan.alreadyTagged += 1;
      continue;
    }
    // Hand-tagged to the same account is no conflict: link it to the roster.
    const sameAccount =
      Boolean(match.userId) && cluster.userId === match.userId && !cluster.nameId;
    if (isTaggedByHand(cluster) && !sameAccount) {
      plan.keptManual += 1;
      continue;
    }
    plan.edits.push({
      ...cluster,
      userId: match.userId || undefined,
      teamPlayerId,
      teamV2Id: cluster.teamV2Id,
      name: autoTagClusterName(cluster, match),
    });
    plan.tagged += 1;
  }
  return plan;
};

/**
 * Roster rows in the shape the session page names players with
 * (`TaggedPlayer.teamPlayer`). `playerId` is the roster row id, the same id
 * auto-tag saves as a cluster's `teamPlayerId`, so the two line up.
 */
export const toTeamPlayers = (
  rows: SessionTeamRosterPlayer[],
  teamId: string | undefined,
): FBTeamPlayer[] =>
  rows
    .filter((row) => row.visible !== 0)
    .map((row) => {
      const jersey = normalizeRosterJersey(row.jersey);
      return {
        playerId: String(row.id),
        teamId: teamId ?? "",
        userId: row.userId || undefined,
        name: row.name?.trim() || undefined,
        firstName: row.firstName,
        lastName: row.lastName,
        position: row.position ?? undefined,
        // FBTeamPlayer says number; a player with no number has none.
        jersey: jersey as number,
        createdAt: 0,
        updatedAt: 0,
      };
    });
